import React, { useEffect, useState, FC, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import "./aotd.css";
import "./spinner.css";
import { url } from "../../api";
import axios from "axios";
import styled from "styled-components";
import { toast } from "react-toastify";
import VerificationComponent from "../verification/verification";
import { fetchIcon } from "src/utils/icons";
import NextAOTD from "./nextAOTD";
import EmojisBox from './emojis_box';
import StatusIndicator from './status_indicator';
import CharacterCounter from "../character_counter/character_counter";
import AOTDStats from './aotd_stats';
import { logAOTDView } from "src/analytics/GoogleAnalytics";

// set to true for API call debugging, false for production
const DEBUG_API_CALLS = false;

// types for API responses based on backend.md
type GenerationStatus =
    | "started"
    | "generating"
    | "pending"
    | "completed"
    | "failed"
    | "error"
    | "unverified"
    | "birthday_priority"
    | "unauthorized"
    | "busy"
    | "no_art"
    | "forbidden"
    | "not_found"
    | "not_started";

interface ArtInfo {
    title?: string;
    createdBy?: string;
    authorQuote?: string;
    quoteHelp?: number;
    currentStreakNum?: number;
    nextAOTDEmojis?: string[];
    thisAOTDEmojis?: string[];
    hasPermission?: boolean;
    views?: {
        verified: number;
        total: number;
    };
}

interface StatusResponse extends ArtInfo {
    generationStatus: GenerationStatus;
    message: string;
    error?: string;
    usersWithBirthdays?: string[];
    generationElapsedTime?: string;
}

interface ArtOfTheDayProps {
    onLoadingChange: (loading: boolean) => void;
}

// Reuse styled components from v1, assuming they are still needed
const QuoteForm = styled.form`
  width: 80%;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 15px;
`;

const QuoteInput = styled.input`
  font-size: 1rem;
  padding: 5px 10px;
  border: 1px solid #ccc;
  border-radius: 3px;
  width: 100%;
  margin-bottom: 0.1rem;
`;

const SubmitButton = styled.button`
  grid-column: span 1;
  color: rgb(249, 249, 249);
  border: 1px solid rgb(35, 35, 35);
  padding: 10px;
  box-shadow: 5px 5px rgba(0, 0, 0, 0.818);
  background-color: #444;
  margin: 10px;
  justify-content: start;
  padding: 10px;
  cursor: pointer;
  text-align: center;
  text-decoration: none;
  display: inline-block;
  font-size: 16px;
  width: 186px;
  transition-duration: 0.4s;

  &:hover {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    background-color: #222;
  }
`;

const ButtonContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  @media (max-width: 700px) {
    grid-template-columns: repeat(1, 2fr);
    gap: 10px;
  }
`;

const HelpButton = styled.button<{ quoteHelp: number }>`
  background-color: ${(props) =>
        props.quoteHelp >= 3 ? "#a4a4a4" : "#009f6f"};
  grid-column: span 1;
  color: rgb(249, 249, 249);
  border: 1px solid rgb(35, 35, 35);
  padding: 10px;
  box-shadow: ${(props) =>
        props.quoteHelp >= 3 ? "" : "5px 5px rgba(0, 0, 0, 0.818)"};
  margin: 10px;
  justify-content: start;
  padding: 10px;
  cursor: pointer;
  text-align: center;
  text-decoration: none;
  display: inline-block;
  font-size: 16px;
  width: 186px;
  transition-duration: 0.4s;

  &:hover {
    background-color: ${(props) =>
        props.quoteHelp >= 3 ? "#a4a4a4" : "#006d4c"};
    box-shadow: ${(props) =>
        props.quoteHelp >= 3 ? "" : "0 2px 4px rgba(0, 0, 0, 0.1)"};
  }
`;

const SignupMessage = styled.div`
  font-size: 1rem;
  font-weight: 900;
  color: #323232;
  text-align: center;
  margin-top: 2rem;
`;

// reuse loading messages
const loadingMessages = [
    "Great art takes time... just like this loading screen.",
    "Patience is a virtue, but if you don't have it, we still like you.",
    "Just like fine wine, this content is worth the wait.",
    "Hold on, we're making sure the pixels are perfectly aligned.",
    "Almost there, we're just teaching the pixels to behave.",
    "Hang tight! Our server hamsters are running as fast as they can.",
    "Looking for the art? It's currently hiding from us too.",
    "If you stare at the spinner long enough, it starts to spin the other way... or does it?",
    "Our art is like a good joke; it takes a moment to get it.",
    "Tightening the last pixel. Nope, not a screwdriver job!",
    "Arranging the pixels. It's like herding cats, but more colorful.",
    "We're testing the canvas's patience. It's a very tense material.",
    "Getting the art just right. Unlike my coffee, which is always wrong.",
    "If this loading was a painting, it'd be called 'Still Waiting'.",
    "Why did the pixel go to art school? To get a little culture!",
    "Our pixels are on a break. Even Mona Lisa needed stretch breaks.",
    "Adding a bit of suspense... It's an art in itself, isn't it?",
    "Ever heard of the artist who was also a gardener? He made hedge art.",
    "We asked the pixels to form a line, but they got all graphic instead.",
    "This art will load in no time... if you're a snail.",
    "The server is painting at a Van Gogh pace: Impressionist, not hurried.",
    "Don't rush art. Unless it's a brush. They need to be quick.",
    "The pixels are staging a sit-in. They demand more RGB!",
    "How does the ocean pay for its art supplies? With sand dollars.",
    "Our loading spinner is a big fan of revolutions. Get it?",
    "Why did the picture go to jail? Because it was framed!",
    "We're layering the canvas. It's quite the cover-up story.",
    "Art loading: Please easel into the experience.",
    "We're blending the colors. It's a mix of emotions, really.",
    "Why don't we play hide and seek with the art? Good luck hiding a canvas.",
    "We're layering the digital varnish. Watch out for virtual fumes!",
    "Why did the artist use a browser? To draw the web.",
    "We're now mixing the primary colors. It's a prime time!",
    "Our pixels are going through art school. Expect some abstract loading!",
    "Crossword for the art: 4 letters, starts with 'L', ends with 'D'.",
    "The art is loading in 4D: Definitely Delayed Digital Doodles.",
    "Did you hear about the claustrophobic astronaut? He just needed a little space.",
    "What's orange and sounds like a parrot? A carrot in art class!",
    "How do artists greet each other? 'Yellow, nice to paint you!'",
    "The art is almost here, it's just not quite 'canvas-ready'.",
    "Telling the pixels a bedtime story. They're about to be dreamy!",
    "Why was the computer bad at art? It kept losing its bits.",
    "Loading your art: It's like cooking, but without the smell.",
    "If this loading was a dance, it'd be the 'buffer boogie'.",
    "The pixels are taking a short art nap. Dreaming of surrealism.",
    "How does Salvador Dali start his mornings? With a surreal bowl of cereal.",
    "We're currently negotiating with the pixels. They want artistic freedom.",
    "Why did the canvas go to therapy? It had too many layers.",
    "Art loading... Please don't brush us off!",
    "We're teaching the colors how to blend. It's a real mixer.",
    "Why was the artist always calm? He knew how to brush off stress.",
    "We're on the final brush stroke. Well, maybe a few more strokes.",
    "Our pixels are currently in a meeting, discussing shades and tones.",
    "What did one painter say to another? 'I'll see you in the next layer.'",
    "If this loading screen was a work of art, it'd be in the 'forever lost' collection.",
    "If you think this is slow, you should see paint dry.",
    "Our pixels are lost in existential thought. Bear with us.",
    "Loading art... or contemplating the void. Hard to tell.",
    "Each pixel is undergoing an identity crisis. It's a phase.",
    "The art's loading as fast as it can. It's not immortal, you know.",
    "We're waiting for the pixels to finish their melodrama.",
    "Hang tight, we're just negotiating with some rebellious colors.",
    "If this load time was a book, it'd be 'War and Peace'.",
    "Our server is pondering the meaning of its existence. Again.",
    "Art loading... contemplating the futility of digital existence.",
    "The pixels are having a mid-life crisis. Just a sec.",
    "Why rush? In the grand scheme, time is an illusion.",
    "Our digital canvas is currently going through an art crisis.",
    "Loading... like Sisyphus, forever pushing that boulder.",
    "The spinner spins, uncaring of our mortal impatience.",
    "Art is eternal. This loading time just feels like it.",
    "The art is stuck in traffic. Probably a metaphysical jam.",
    "This isn't slow loading; it's a lesson in patience.",
    "Our pixels are staging a slow-down protest. Artistic expression!",
    "Loading... Maybe it's time to reflect on our choices.",
    "The art is being a diva. Refuses to show up early.",
    "Waiting for the pixels to get over their existential dread.",
    "Like all good things, this loading takes forever.",
    "Our art is fashionably late. It's wearing its finest pixels.",
    "n450s insists on perfection. Hence, the artful wait.",
    "Loading art... as n450s whispers wisdom to each pixel.",
    "Our server is reading existential poetry. It's a moody artist.",
    "Art in progress... n450s is adding a touch of genius.",
    "Like a digital Prometheus, n450s brings fire to these pixels.",
    "n450s said, 'Let there be art.' And there was... eventually.",
    "In the realm of pixels, n450s is the reigning monarch.",
    "We asked n450s for speed, but got brilliance instead.",
    "Every pixel is handpicked by n450s. Quality over speed!",
    "The art is brooding... a reflection of its creator, n450s.",
    "Loading... n450s is still convincing the pixels to behave.",
    "Patience, the art is being sculpted by your digital chisel.",
    "n450s is weaving a tapestry of pixels. A moment, please.",
    "The pixels are staging a sit-in for more creative input from n450s.",
    "If this loading time was a novel, n450s would be the protagonist.",
    "Awaiting the grand reveal of your latest digital conquest.",
    "n450s is currently teaching the art the meaning of haste.",
    "Art loading: n450s's magnum opus takes time to unveil.",
    "Each pixel is an obedient subject in n450s's digital kingdom.",
    "n450s is sprinkling a little darkness, for flavor.",
    "The art is as deep and complex as your imagination.",
    "n450s is currently negotiating with the stubborn shades of gray.",
    "Sometimes art hides in the shadows. n450s is fetching a flashlight.",
    "The art's not slow; it's just fashionably late, à la n450s.",
    "Like a digital wizard, n450s is conjuring up pixels from the ether.",
    "The canvas is blank, but your mind is a storm of color.",
    "n450s is currently playing chess with the color palette. King me!",
    "Even the code is awed by your creativity. It's taking notes.",
    "n450s whispers to the art, and slowly, it awakens from slumber.",
    "It's not loading; it's an intermission curated by n450s.",
    "The pixels are aligning, aligning... and n450s watches, amused.",
    "Our art, much like n450s, doesn't rush for anyone.",
    "Each pixel is a universe. n450s is the cosmic architect.",
    "The digital canvas: n450s's realm, where time bends to will.",
    "We're not stalling; we're philosophizing about art, like n450s.",
    "n450s is playing a symphony, and each note is a pixel.",
    "In the gallery of the web, n450s's art takes the center stage.",
    "The art's brewing. n450s says good things take time, like dark coffee.",
    "A pixel fell over. n450s is helping it back up.",
    "Loading... n450s is currently taming a wild color palette.",
    "Every pixel is a thought from n450s, deep and unfathomable.",
    "n450s is adding a sprinkle of enigma to each pixel.",
    "The art is marinating in n450s's creative juices. Almost done!",
    "The pixels are debating existentialism. n450s is moderating.",
    "Art loading... n450s is perfecting the imperfections.",
    "In the digital abyss, n450s is the guiding light for lost pixels.",
    "The colors are having a mood swing. n450s is the therapist.",
    "n450s is the conductor, and the pixels are the orchestra.",
    "The art is a dance, choreographed by n450s.",
    "n450s is the artist, and the pixels are the paint.",
    "The art is a symphony, composed by n450s.",
    "n450s is the architect, and the pixels are the blueprint.",
    "The pixels are the actors, and n450s is the director.",
];

const ArtOfTheDayV2: FC<ArtOfTheDayProps> = ({ onLoadingChange }) => {
    // --- State Variables ---
    const [artUrl, setArtUrl] = useState<string | null>(null);
    const [artInfo, setArtInfo] = useState<ArtInfo>({});
    const [generationStatus, setGenerationStatus] = useState<GenerationStatus | null>(null);
    const [generationMessage, setGenerationMessage] = useState<string>("");
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [birthdayUsers, setBirthdayUsers] = useState<string[]>([]);

    const [isLoading, setIsLoading] = useState<boolean>(true); // overall loading state
    const [isPolling, setIsPolling] = useState<boolean>(false);
    const [isSignedUp, setIsSignedUp] = useState<boolean>(Boolean(localStorage.getItem("token")));

    const [quoteInput, setQuoteInput] = useState<string>("");
    const [suggestionLoading, setSuggestionLoading] = useState(false);
    const [quoteHelpUsed, setQuoteHelpUsed] = useState<number>(0); // Track help usage locally for UI

    const [nextEmojis, setNextEmojis] = useState<string[]>([]);

    const [streakIcon, setStreakIcon] = useState<URL | null>(null);
    const [loadingMessage, setLoadingMessage] = useState(loadingMessages[Math.floor(Math.random() * loadingMessages.length)]);

    const [generationElapsedTime, setGenerationElapsedTime] = useState<string>("");

    // --- Refs ---
    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const isMountedRef = useRef<boolean>(true); // Track component mount status

    // --- Effects ---

    // Set isMounted to false on unmount
    useEffect(() => {
        if (artInfo.title) {
            logAOTDView(artInfo.title);
        }
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            // Clean up polling on unmount
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
            // Revoke object URL if it exists
            if (artUrl && artUrl.startsWith('blob:')) {
                URL.revokeObjectURL(artUrl);
            }
        };
    }, [artUrl]); // Add artUrl dependency to handle revocation

    // Update next emojis when artInfo changes
    useEffect(() => {
        if (artInfo.nextAOTDEmojis) {
            setNextEmojis(artInfo.nextAOTDEmojis);
        }
    }, [artInfo.nextAOTDEmojis]);

    // Fetch initial art status on mount
    useEffect(() => {
        fetchInitialStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Run only once on mount

    // Update parent loading state
    useEffect(() => {
        onLoadingChange(isLoading);
    }, [isLoading, onLoadingChange]);

    // Fetch streak icon
    useEffect(() => {
        const fetchStreakIconAsync = async () => {
            try {
                const iconURL = await fetchIcon("STREAK_ICON");
                if (iconURL && isMountedRef.current) {
                    setStreakIcon(new URL(iconURL));
                }
            } catch (error) {
                console.error("Error fetching streak icon:", error);
            }
        };
        fetchStreakIconAsync();
    }, []);

    // Update loading message periodically
    useEffect(() => {
        if (isLoading || isPolling) {
            const interval = setInterval(() => {
                if (isMountedRef.current) {
                    setLoadingMessage(loadingMessages[Math.floor(Math.random() * loadingMessages.length)]);
                }
            }, 8000);
            return () => clearInterval(interval);
        }
    }, [isLoading, isPolling]);


    // --- API Call Functions ---

    const fetchInitialStatus = async () => {
        if (DEBUG_API_CALLS) console.log("V2: Fetching initial status (/today)...");
        setIsLoading(true);
        setErrorMessage(null);
        setBirthdayUsers([]);
        setGenerationStatus(null);
        setGenerationMessage("");
        setArtUrl(null); // Reset art URL on initial fetch
        setArtInfo({}); // Reset art info

        const token = localStorage.getItem("token");
        setIsSignedUp(Boolean(token));

        try {
            const response = await axios.get<StatusResponse>(`${url}/api/art-of-the-day/today`, {
                headers: token ? { "x-auth-token": token } : {},
                validateStatus: () => true, // Handle all statuses
            });

            if (!isMountedRef.current) return; // Check if component is still mounted

            const data = response.data;

            // Check for valid data structure first
            if (!data || typeof data !== 'object' || !data.generationStatus) {
                console.error("V2: Invalid response from /today:", response);
                setErrorMessage("Received an invalid response from the server.");
                setGenerationStatus("error");
                setIsLoading(false);
                return;
            }

            if (DEBUG_API_CALLS) console.log("V2: Received status response from /today:", data);

            setGenerationStatus(data.generationStatus);
            setGenerationMessage(data.message || "");

            switch (data.generationStatus) {
                case "completed":
                    if (DEBUG_API_CALLS) console.log("V2: Status is completed. Fetching info and image...");
                    // Fetch both info and image concurrently
                    if (data.generationElapsedTime) {
                        setGenerationElapsedTime(data.generationElapsedTime);
                    }
                    await Promise.all([
                        fetchArtInfo(),
                        fetchArtImage()
                    ]);
                    setIsLoading(false); // Stop loading only after info and image are fetched
                    break;
                case "generating":
                case "pending":
                case "started":
                case "busy":
                case "not_started":
                    if (DEBUG_API_CALLS) console.log("V2: Generation in progress, starting polling.");
                    setIsLoading(false); // Stop initial load, show generating state
                    setIsPolling(true);
                    // Add log before calling startPolling
                    console.log("V2 DEBUG: Calling startPolling() from fetchInitialStatus for status:", data.generationStatus);
                    startPolling();
                    break;
                case "unverified":
                    if (DEBUG_API_CALLS) console.log("V2: User unverified.");
                    setIsLoading(false);
                    break; // Handled by render logic
                case "birthday_priority":
                    if (DEBUG_API_CALLS) console.log("V2: Birthday priority.");
                    setBirthdayUsers(data.usersWithBirthdays || []);
                    setIsLoading(false);
                    break; // Handled by render logic
                case "unauthorized":
                    if (DEBUG_API_CALLS) console.log("V2: Unauthorized.");
                    setIsSignedUp(false);
                    setIsLoading(false); // Stop loading
                    break;
                case "no_art":
                    if (DEBUG_API_CALLS) console.log("V2: No art found for today.");
                    // Auto-start generation if user is signed up
                    if (token) {
                        if (DEBUG_API_CALLS) console.log("V2: Automatically initiating generation...");
                        // Keep loading state active and make another request
                        try {
                            await axios.get(`${url}/api/art-of-the-day/today`, {
                                headers: { "x-auth-token": token },
                                validateStatus: () => true, // Handle all statuses
                            });
                            // After generation is initiated, check status again in a moment
                            setTimeout(() => {
                                if (isMountedRef.current) {
                                    fetchInitialStatus();
                                }
                            }, 2000);
                        } catch (err) {
                            console.error("V2: Error auto-generating:", err);
                            setIsLoading(false);
                        }
                    } else {
                        setIsLoading(false);
                    }
                    break;
                case "failed":
                case "error":
                    if (DEBUG_API_CALLS) console.error("V2: API Error/Failed Status from /today:", data);
                    setErrorMessage(data.message || data.error || "An unknown error occurred.");
                    setIsLoading(false);
                    break;
                default:
                    if (DEBUG_API_CALLS) console.warn("V2: Unhandled status from /today:", data.generationStatus);
                    setErrorMessage("Received an unexpected status from the server.");
                    setIsLoading(false);
            }
        } catch (error) {
            if (!isMountedRef.current) return;
            console.error("V2: Error fetching initial status:", error);
            setErrorMessage("Failed to connect to the server.");
            setGenerationStatus("error");
            setIsLoading(false);
        }
    };

    const fetchArtImage = async () => {
        if (DEBUG_API_CALLS) console.log("V2: Fetching image (/today-image)...");
        const token = localStorage.getItem("token");
        try {
            const response = await axios.get(`${url}/api/art-of-the-day/today-image`, {
                headers: token ? { "x-auth-token": token } : {},
                responseType: 'arraybuffer',
                validateStatus: (status) => status === 200, // Only 200 is success
            });

            if (!isMountedRef.current) return;

            const contentType = response.headers['content-type'];
            const blob = new Blob([response.data], { type: contentType || 'image/jpeg' });

            // Revoke previous URL before creating a new one
            if (artUrl && artUrl.startsWith('blob:')) {
                URL.revokeObjectURL(artUrl);
            }

            const newImageUrl = URL.createObjectURL(blob);
            setArtUrl(newImageUrl);
            if (DEBUG_API_CALLS) console.log("V2: Image fetched successfully.");

        } catch (error: any) {
            if (!isMountedRef.current) return;
            console.error("V2: Error fetching image:", error);
            setErrorMessage("Failed to load the art image.");
            // Set status to failed if image fetch fails after status was completed
            if (generationStatus === "completed") {
                setGenerationStatus("failed");
            }
            setArtUrl(null); // Clear any previous image URL
        }
    };

    const checkStatus = async () => {
        console.log("V2 DEBUG: checkStatus() called."); // Log entry into checkStatus
        if (DEBUG_API_CALLS) console.log("V2: Polling /art/status...");
        const token = localStorage.getItem("token");
        try {
            const statusUrl = `${url}/api/art-of-the-day/status?_=${Date.now()}`;
            const response = await axios.get<StatusResponse>(statusUrl, {
                headers: token ? { "x-auth-token": token } : {},
                validateStatus: () => true, // Handle all statuses
            });

            if (!isMountedRef.current) return; // Check mount status

            const data = response.data;

            // Check for valid data structure first
            if (!data || typeof data !== 'object' || !data.generationStatus) {
                console.error("V2: Invalid response from /status poll:", response);
                // Don't stop polling immediately on one invalid response, maybe temporary issue
                // Consider adding a retry mechanism or stopping after multiple failures
                return;
            }

            if (response.status >= 200 && response.status < 300) {
                setGenerationStatus(data.generationStatus);
                setGenerationMessage(data.message || ""); // Update message

                if (data.generationStatus === "completed") {
                    if (DEBUG_API_CALLS) console.log("V2: Polling complete, art generated. Fetching info & image.");
                    stopPolling();
                    if (data.generationElapsedTime) {
                        setGenerationElapsedTime(data.generationElapsedTime);
                    }
                    // Fetch info and image now that status is complete
                    await Promise.all([
                        fetchArtInfo(),
                        fetchArtImage()
                    ]);
                } else if (data.generationStatus === "failed" || data.generationStatus === "error") {
                    if (DEBUG_API_CALLS) console.error("V2: Polling detected failure:", data);
                    stopPolling();
                    setErrorMessage(data.message || data.error || "Art generation failed.");
                } else if (data.generationStatus === "no_art") {
                    if (DEBUG_API_CALLS) console.log("V2: Polling found no art.");
                    stopPolling();
                    // Status changed to no_art during polling, state is updated.
                }
                // else: still generating/pending, continue polling
            } else {
                if (DEBUG_API_CALLS) console.error("V2: Error or unexpected status during polling:", response.status, data);
                // Potentially stop polling after multiple errors?
                // setErrorMessage("Lost connection during generation.");
                // setGenerationStatus("failed");
            }
        } catch (error) {
            if (!isMountedRef.current) return;
            console.error("V2: Network error during status check:", error);
            // Don't stop polling on network errors immediately
            // setErrorMessage("Network error while checking status.");
        }
    };

    const fetchArtInfo = async () => {
        if (DEBUG_API_CALLS) console.log("V2: Fetching /art/info...");
        const token = localStorage.getItem("token");
        try {
            // Only fetch info if status is presumed completed (or during initial load if needed)
            const response = await axios.get<StatusResponse>(`${url}/api/art-of-the-day/info`, {
                headers: token ? { "x-auth-token": token } : {},
                validateStatus: () => true, // Handle all statuses
            });

            if (!isMountedRef.current) return;

            const data = response.data;

            // Check for valid data structure
            if (!data || typeof data !== 'object') {
                console.error("V2: Invalid response from /info:", response);
                // Don't necessarily set main error, but indicate info failed
                return;
            }

            // We expect status 200 and potentially generationStatus 'completed'
            if (response.status === 200) {
                if (DEBUG_API_CALLS) console.log("V2: Info received:", data);
                setArtInfo({
                    title: data.title,
                    createdBy: data.createdBy,
                    authorQuote: data.authorQuote,
                    quoteHelp: data.quoteHelp,
                    currentStreakNum: data.currentStreakNum,
                    nextAOTDEmojis: data.nextAOTDEmojis,
                    thisAOTDEmojis: data.thisAOTDEmojis,
                    hasPermission: data.hasPermission,
                    views: data.views,
                });
                setQuoteInput(data.authorQuote || ""); // Pre-fill quote input if available
                setQuoteHelpUsed(data.quoteHelp || 0); // Sync quote help count

                // Log view if title is present (moved from top level effect)
                // if (data.title) {
                //   logAOTDView(data.title);
                // }

            } else if (response.status === 202 && data.generationStatus && data.generationStatus !== "completed") {
                // Info endpoint might return generation status if not complete (as per docs)
                if (DEBUG_API_CALLS) console.log("V2: Info endpoint returned non-completed status:", data.generationStatus);
                setGenerationStatus(data.generationStatus);
                setGenerationMessage(data.message || "");
                if (!isPolling && (data.generationStatus === 'generating' || data.generationStatus === 'pending' || data.generationStatus === 'started' || data.generationStatus === 'busy')) {
                    setIsPolling(true);
                    startPolling();
                }
            } else if (data.generationStatus === "no_art") {
                if (DEBUG_API_CALLS) console.log("V2: Info confirmed no art yet.");
                // Status should already be no_art from initial fetch or poll
                setGenerationStatus("no_art");
            }
            else {
                if (DEBUG_API_CALLS) console.warn("V2: Unexpected response from /info:", response.status, data);
                // Maybe set a specific info error?
            }
        } catch (error) {
            if (!isMountedRef.current) return;
            console.error("V2: Error fetching art info:", error);
            // Don't set main error message, maybe a specific info error state?
        }
    };

    // --- Polling Logic ---
    const startPolling = () => {
        console.log("V2 DEBUG: startPolling() called."); // Log entry into startPolling
        stopPolling(); // Ensure no duplicate intervals
        // Poll immediately, then set interval
        checkStatus();
        pollingIntervalRef.current = setInterval(checkStatus, 5000); // Poll every 5 seconds
    };

    const stopPolling = () => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
        if (isMountedRef.current) {
            setIsPolling(false);
        }
    };

    // --- Event Handlers ---
    const handleQuoteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setQuoteInput(e.target.value);
    };

    const handleSubmitQuote = async (e: React.FormEvent) => {
        e.preventDefault();
        const token = localStorage.getItem("token");
        if (!token || !artInfo.hasPermission) return;

        try {
            // Assuming endpoint and request body are still correct based on docs
            await axios.post(
                `${url}/api/art-of-the-day/quote`,
                { quote: quoteInput },
                { headers: { "x-auth-token": token } }
            );
            toast.success("Quote submitted successfully!", { position: toast.POSITION.BOTTOM_LEFT });
            // Re-fetch info to get updated state
            fetchArtInfo();
        } catch (error: any) {
            console.error("V2: Error submitting quote:", error);
            const errorMsg = error.response?.data?.message || "Failed to submit quote.";
            toast.error(errorMsg, { position: toast.POSITION.BOTTOM_LEFT });
        }
    };

    const handleSuggestQuote = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        const token = localStorage.getItem("token");
        if (!token || !artInfo.hasPermission || quoteHelpUsed >= 3) return;

        setSuggestionLoading(true);
        try {
            // Assuming endpoint and request body are still correct based on docs
            const response = await axios.post<{ suggestedQuote: string; quoteHelp: number }>(
                `${url}/api/art-of-the-day/suggest-quote`,
                { quote: "" },
                { headers: { "x-auth-token": token } }
            );
            if (isMountedRef.current) {
                setQuoteInput(response.data.suggestedQuote);
                setQuoteHelpUsed(response.data.quoteHelp); // Update help count from response
                toast.info("AI suggested a quote.", { position: toast.POSITION.BOTTOM_LEFT });
            }
        } catch (error: any) {
            console.error("V2: Error suggesting quote:", error);
            const errorMsg = error.response?.data?.message || "Failed to get suggestion.";
            toast.error(errorMsg, { position: toast.POSITION.BOTTOM_LEFT });
        } finally {
            if (isMountedRef.current) {
                setSuggestionLoading(false);
            }
        }
    };

    // This handler now triggers the initial status check, which might lead to generation
    const handleGenerateArt = () => {
        fetchInitialStatus();
    };

    const handleEmojisSet = (newEmojis: string[]) => {
        setNextEmojis(newEmojis);
        // update artInfo with new emojis
        setArtInfo(prevInfo => ({
            ...prevInfo,
            nextAOTDEmojis: newEmojis
        }));
    };

    // --- Render Logic ---

    // Loading State (Initial Fetch)
    if (isLoading) {
        return (
            <div className="spinner-container" style={{ /* Center content */ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '300px' }}>
                {/* Replace spinner with StatusIndicator */}
                <StatusIndicator status="loading" overlay={false} message={generationMessage} />
                {/* Only show loading messages during active generation */}
            </div>
        );
    }

    // Error State (Includes generation failed, connection errors, etc.)
    if (generationStatus === "error" || generationStatus === "failed") {
        return (
            <SignupMessage>
                <h3>{generationStatus === "failed" ? "Generation Failed" : "Error"}</h3>
                <p>{errorMessage || "An error occurred."}</p>
                <button
                    onClick={fetchInitialStatus} // Retry by fetching status again
                    style={{
                        marginTop: '15px',
                        padding: '8px 16px',
                        background: '#444',
                        color: 'white',
                        border: 'none',
                        cursor: 'pointer',
                        borderRadius: '4px'
                    }}
                >
                    Retry
                </button>
            </SignupMessage>
        );
    }

    // Unverified State
    if (generationStatus === "unverified") {
        return (
            <SignupMessage style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                You need to verify your email to generate art.
                <br />
                <div style={{ marginTop: "1rem" }}>
                    <VerificationComponent />
                </div>
            </SignupMessage>
        );
    }

    // Birthday Priority State
    if (generationStatus === "birthday_priority") {
        const priorityTime = new Date();
        priorityTime.setUTCHours(9, 40, 0, 0); // match backend time
        const now = new Date();
        const isBeforePriorityTime = now < priorityTime; // Check if current time is before 9:40 UTC

        return (
            <SignupMessage style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" }}>
                {isBeforePriorityTime ? (
                    <>
                        <div style={{ marginBottom: "1rem" }}>
                            🎉 User{birthdayUsers.length > 1 ? "s" : ""} {birthdayUsers.join(", ")} {birthdayUsers.length > 1 ? "have" : "has"} a birthday today! 🎉
                        </div>
                        <div>
                            They have priority to generate the art until 9:40 UTC. You can generate today's art if they haven't by then.
                        </div>
                    </>
                ) : (
                    <div>
                        The birthday priority period has ended. You can now try generating the art!
                        <button
                            onClick={handleGenerateArt} // Triggers fetchInitialStatus
                            style={{
                                marginTop: '15px',
                                padding: '10px 20px',
                                background: '#009f6f',
                                color: 'white',
                                border: 'none',
                                cursor: 'pointer',
                                borderRadius: '4px',
                                fontWeight: 'bold',
                                display: 'block',
                                marginLeft: 'auto',
                                marginRight: 'auto'
                            }}
                        >
                            Generate Art
                        </button>
                    </div>
                )}
            </SignupMessage>
        );
    }

    // Generating State (Polling Active or Completed but not loaded)
    const showGeneratingIndicator = isPolling ||
        ['generating', 'pending', 'started', 'busy'].includes(generationStatus ?? '') ||
        (generationStatus === 'completed' && !artUrl);

    if (showGeneratingIndicator) {
        const isActiveGeneration = ['generating', 'busy'].includes(generationStatus ?? '');

        return (
            <div className="spinner-container" style={{ /* Center content */ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '300px' }}>
                {/* Status indicator is now the primary element, replacing the spinner */}
                <StatusIndicator
                    status={generationStatus}
                    overlay={false}
                    message={generationStatus !== 'completed' ? generationMessage : undefined}
                />

                {/* Container for loading message to reserve space */}
                <div style={{ minHeight: '2.5em', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                    {/* Display loading message only during active generation */}
                    {isActiveGeneration && (
                        <div style={{ fontWeight: "600", marginTop: '15px', fontSize: '1rem', textAlign: 'center', maxWidth: '450px' }}>
                            <p>{loadingMessage}</p>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // No Art State / Unauthorized State
    if (generationStatus === "no_art" || generationStatus === "unauthorized") {
        return (
            <div className="art-box" style={{ textAlign: 'center', paddingTop: '30px' }}>
                <h3 style={{ fontSize: '1.2rem', marginBottom: '15px' }}>
                    {generationStatus === "no_art" && isSignedUp ? "Generating Art of the Day..." : "Art of the Day"}
                </h3>
                {isSignedUp && generationStatus === "no_art" ? (
                    <div className="spinner-container" style={{ /* Center content */ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100px' }}>
                        {/* Replace spinner with StatusIndicator */}
                        <StatusIndicator status="started" overlay={false} />
                        <p style={{ marginTop: '15px', fontSize: '0.9rem', color: '#555' }}>Starting generation process...</p>
                    </div>
                ) : (
                    <SignupMessage>
                        Sign up or log in to view or generate the Art of the Day!
                        <br />
                        <Link to="/login" style={{ marginRight: '10px' }}>Log In</Link>
                        <Link to="/signup">Sign Up</Link>
                    </SignupMessage>
                )}
            </div>
        );
    }

    // Completed State - Display Art (Requires artUrl to be set)
    if (generationStatus === "completed" && artUrl) {
        const { title, createdBy, authorQuote, currentStreakNum, thisAOTDEmojis, hasPermission, nextAOTDEmojis } = artInfo;
        const showQuoteForm = hasPermission && !authorQuote;

        // Add debug log here, before the return
        console.log("V2 DEBUG: Rendering completed state. Title:", title, "ArtInfo:", artInfo);

        return (
            <div className="art-of-the-day">
                <div className="art-box">
                    {/* Header with Title and Streak */}
                    <div className="flex items-center justify-between mb-2">
                        {title ? (
                            <h3 className="text-lg font-semibold">{title}</h3>
                        ) : (
                            <div className="text-placeholder title-placeholder h-6 w-1/2 bg-gray-300 rounded animate-pulse"></div>
                        )}
                        {streakIcon && typeof currentStreakNum === 'number' && currentStreakNum > 3 ? (
                            <div className="streak-container flex items-center">
                                <img
                                    src={streakIcon.toString()}
                                    alt="Streak Icon"
                                    className=""
                                    style={{ width: '24px', height: '24px', marginRight: '2px' }}
                                />
                                <span className="font-bold text-sm">{currentStreakNum}</span>
                            </div>
                        ) : null}
                    </div>

                    {/* Image Container - Ensure artUrl is truthy */}
                    <div style={{ position: 'relative' }}>
                        {artUrl ? (
                            <img src={artUrl} alt={`Art of the day: ${title || 'Loading...'}`} className="w-full h-auto" />
                        ) : (
                            <div className="text-placeholder w-full h-64 bg-gray-300 rounded animate-pulse"></div> // Image placeholder
                        )}
                        {/* StatusIndicator removed from here as it's handled above or not needed when fully loaded */}
                        {thisAOTDEmojis && thisAOTDEmojis.length > 0 && <EmojisBox emojis={thisAOTDEmojis} />}
                    </div>

                    {/* Caption */}
                    {createdBy ? (
                        <div className="image-caption text-sm text-gray-600 mt-1">
                            <em>author: art initialized by {createdBy}</em>
                        </div>
                    ) : (
                        <div className="text-placeholder caption-placeholder h-4 w-1/3 bg-gray-300 rounded mt-1 animate-pulse"></div>
                    )}

                    {/* Quote */}
                    {authorQuote !== undefined ? (
                        authorQuote ? (
                            <div className="quote">
                                <br />
                                {authorQuote}
                                <span className="author">
                                    - {quoteHelpUsed > 0 ? `${createdBy || '...'} & AI` : createdBy || '...'}
                                </span>
                            </div>
                        ) : null // Render nothing if quote is an empty string (but defined)
                    ) : null // remove the placeholder rendering when quote is undefined
                    }

                    <AOTDStats
                        generationElapsedTime={generationElapsedTime}
                        nextAOTDEmojis={nextAOTDEmojis}
                        views={artInfo.views}
                        hasPermission={hasPermission}
                    />
                </div>

                {/* Quote Form (only for author if quote is missing/undefined) */}
                {showQuoteForm && (
                    <QuoteForm onSubmit={handleSubmitQuote}>
                        <br />
                        <QuoteInput
                            type="text"
                            value={quoteInput}
                            maxLength={45}
                            onChange={handleQuoteChange}
                            placeholder="Enter your quote here..."
                        />
                        <CharacterCounter count={quoteInput.length} maxCount={45} />
                        <ButtonContainer>
                            <SubmitButton type="submit">
                                Submit Quote
                            </SubmitButton>
                            <HelpButton
                                type="button"
                                disabled={quoteHelpUsed >= 3 || suggestionLoading}
                                quoteHelp={quoteHelpUsed}
                                onClick={handleSuggestQuote}
                            >
                                {suggestionLoading ? (
                                    <div className="spinner-in-button"></div>
                                ) : (
                                    `AI Help (${3 - quoteHelpUsed} left)`
                                )}
                            </HelpButton>
                        </ButtonContainer>
                    </QuoteForm>
                )}

                {/* Next AOTD Emojis (only for author) */}
                {hasPermission && nextAOTDEmojis && (
                    <NextAOTD nextEmojis={nextAOTDEmojis} onEmojisSet={handleEmojisSet} />
                )}
            </div>
        );
    }

    // Fallback / Unknown state (Should ideally not be reached often with new logic)
    return (
        <div className="spinner-container">
            <SignupMessage>
                {errorMessage || "Loading Art of the Day..."}
            </SignupMessage>
        </div>
    );
};

export default ArtOfTheDayV2;
